// @ts-check
// =============================================================================
// js/ui/upsell.js — the one reusable "become a supporter" modal.
//
// WHAT THIS IS (auth-and-tiers.md "Upsell layer")
//   Free-tier and guest players hit paywalled features all over the app:
//   deck brewing, pack opening, hosting past the free cap. Instead of five
//   hand-rolled popups (the old client's :2023 supporter popup, blurred
//   rewards, etc.), every screen calls showUpsell('<feature>') and gets the
//   same styled pitch with feature-specific copy.
// =============================================================================

import { openModal } from './modal.js';
import { store } from '../core/store.js';
import { showLoginOptions, JOIN_URL } from '../app/session.js';
import { isShell, openFullSiteTab } from '../app/shell-mode.js';

/**
 * The PAID membership page. Only the rewards screen's pack-opening upsell (a
 * supporter-only perk) sends players here; every other upsell becomes a FREE
 * member via JOIN_URL. Flagged per-feature with `membership: true` below.
 */
export const PATREON_MEMBERSHIP_URL = 'https://www.patreon.com/smuttygames/join';

/**
 * How many card-art pieces are UNLOCKABLE — the ones a pack can actually drop.
 * Mirrors the economy's roller (server NewRewards): every art variant except the
 * always-owned defaults (chance 100) and the non-droppable credit pieces
 * (chance 0). Counted live from the loaded card table so the pitch stays exact
 * as art is added.
 * @returns {number}
 */
function countUnlockableArts() {
	let n = 0;
	for (const card of store.cards || []) {
		for (const art of card?.newarts || []) {
			const chance = String(art?.chance);
			if (chance !== '0' && chance !== '100') n++;
		}
	}
	return n;
}

/** Feature-specific copy. `membership: true` means "this needs a PAID account",
 *  so the Join button points at the membership page instead of the free post.
 *  `text` may be a function for copy that needs live numbers. */
const COPY = {
	saveDeck: {
		title: 'Build your own decks',
		text: 'Free members can brew custom decks from their whole collection. Signing up takes about a minute.',
	},
	openPack: {
		title: 'Crack open your packs',
		text: () => `Supporters get to open every booster they earn. There are ${countUnlockableArts()} pieces of art to collect.`,
		membership: true,
	},
	host: {
		title: 'Duel real players',
		text: 'Join for free and try your luck against other players*',
		disclaimer: '*dueling other players may lead to getting eaten, and possibly digested',
	},
	default: {
		title: 'Unlock more of the game',
		text: 'Join for free to open this up, and plenty more besides.',
	},
};

/**
 * Shell (itch, Newgrounds…): every blocked feature gets the SAME pitch,
 * because the unlock path is identical — leave the shell, join, log in on
 * the full site. Owner-specified copy: matchmaking lives on the main site,
 * a FREE Patreon membership unlocks online matches + deckbuilding, and the
 * whole cardpool is open from day one (nothing pay-to-win).
 * @returns {void}
 */
function showShellUpsell() {
	const body = document.createElement('div');
	body.innerHTML = `
		<h2 style="font-family:var(--font-display);color:var(--accent-orange);font-size:1.5rem;margin-bottom:0.75rem;">Play Online</h2>
		<p style="color:#fff;font-size:1rem;line-height:1.5;margin-bottom:0.5rem;">Online
			matchmaking is available on the main site, deckbiter.cards.</p>
		<p style="color:#fff;font-size:1rem;line-height:1.5;margin-bottom:0.5rem;">Joining
			the Patreon as a free member unlocks online matches and deckbuilding.</p>
		<p style="color:#fff;font-size:1rem;line-height:1.5;margin-bottom:0.25rem;">The
			whole cardpool is available immediately. No powerful cards are locked away.</p>`;
	openModal({
		body,
		buttons: [
			{ label: 'Join Patreon', onClick: () => window.open(JOIN_URL, '_blank', 'noopener') },
			{ label: 'Open Deckbiter ↗', onClick: openFullSiteTab },
			{ label: 'Close', className: 'modal-dismiss' },
		],
	});
}

/**
 * Show the upsell modal for a blocked feature.
 * @param {string} feature  e.g. 'saveDeck', 'openPack', 'host'.
 * @returns {void}
 */
export function showUpsell(feature) {
	// Portal shells get the one unified pitch — see showShellUpsell.
	if (isShell()) {
		showShellUpsell();
		return;
	}
	const copy = COPY[feature] || COPY.default;
	const isGuest = store.session.isGuest;
	// Paid perks (openPack) point at the membership page; everything else is a
	// free-membership CTA and uses the free "how to play free" post.
	const joinUrl = copy.membership ? PATREON_MEMBERSHIP_URL : JOIN_URL;
	const text = typeof copy.text === 'function' ? copy.text() : copy.text;

	// A tongue-in-cheek "fine print" line, tied to the pitch's asterisk.
	const disclaimer = copy.disclaimer
		? `<p style="color:var(--text-dim);font-size:0.72rem;line-height:1.35;font-style:italic;margin:0.4rem 0 0;">${copy.disclaimer}</p>`
		: '';
	// (Art slot intentionally empty for now — real card art can sit above the
	// title later; we dropped the placeholder emoji.)
	const body = document.createElement('div');
	body.innerHTML = `
		<h2 style="font-family:var(--font-display);color:var(--accent-orange);font-size:1.5rem;margin-bottom:0.75rem;">${copy.title}</h2>
		<p style="color:#fff;font-size:1rem;line-height:1.5;margin-bottom:0.25rem;">${text}</p>
		${disclaimer}`;

	openModal({
		body,
		buttons: [
			{
				label: copy.membership ? 'Join Patreon' : 'Join For Free',
				onClick: () => window.open(joinUrl, '_blank'),
			},
			// Guests may simply need to log into an existing account.
			...(isGuest ? [{ label: 'Log In', onClick: showLoginOptions }] : []),
			// "Never", not "Later": "Later" implies we'll nag you again, which
			// makes the prompt feel pushy. "Never" is a clean, no-pressure out
			// (and a little tongue-in-cheek) that respects the answer and closes
			// the loop — styled quietly so it never competes with the CTA.
			{ label: 'Never', className: 'modal-dismiss' },
		],
	});
}
